import { useState, useEffect } from 'react'
import { 
  collection, 
  doc, 
  setDoc, 
  deleteDoc,
  query, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore'
import { 
  ref, 
  uploadBytesResumable,
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage'
import { db, storage, auth } from '../config/firebase'
import { useAuth } from '../contexts/AuthContext'
import { MediaFile } from '../types/media'
import imageCompression from 'browser-image-compression'
import toast from 'react-hot-toast'

export const useMedia = () => {
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()

  useEffect(() => {
    const mediaRef = collection(db, 'media')
    const q = query(mediaRef, orderBy('uploadedAt', 'desc'))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const mediaData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          uploadedAt: doc.data().uploadedAt?.toDate() || new Date(),
        })) as MediaFile[]

        setMediaFiles(mediaData)
        setLoading(false)
        setError(null)
      },
      (error) => {
        console.error('Error fetching media:', error)
        setError('Failed to load media files')
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  const uploadFile = async (
    file: File, 
    onProgress?: (progress: number) => void
  ): Promise<MediaFile | null> => {
    console.log('Upload attempt - User:', user)
    console.log('Upload attempt - User email:', user?.email)
    console.log('Upload attempt - User UID:', user?.uid)
    console.log('Current auth state:', auth.currentUser)
    
    if (!user) {
      console.error('No user found during upload attempt')
      toast.error('You must be logged in to upload files')
      return null
    }

    // Double-check that the auth state is consistent
    if (!auth.currentUser) {
      console.error('Auth currentUser is null despite user context being set')
      toast.error('Authentication error. Please log in again.')
      return null
    }

    // Small delay to ensure auth state is fully propagated
    await new Promise(resolve => setTimeout(resolve, 100))

    try {
      const isVideo = file.type.startsWith('video/')
      
      // Compress image if it's an image file
      let fileToUpload = file
      if (file.type.startsWith('image/')) {
        const options = {
          maxSizeMB: 2,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        }
        
        try {
          fileToUpload = await imageCompression(file, options)
          console.log('Image compressed:', {
            original: file.size,
            compressed: fileToUpload.size,
            reduction: Math.round((1 - fileToUpload.size / file.size) * 100) + '%'
          })
        } catch (compressionError) {
          console.warn('Image compression failed, using original:', compressionError)
          fileToUpload = file
        }
      }

      // Generate unique filename
      const timestamp = Date.now()
      const fileExtension = file.name.split('.').pop()
      const filename = `${timestamp}_${Math.random().toString(36).substr(2, 9)}.${fileExtension}`
      
      // For videos, upload to media-uploads folder for processing
      // For images, upload directly to media folder
      const uploadPath = isVideo ? `media-uploads/${filename}` : `media/${filename}`
      console.log('Upload path:', uploadPath)
      console.log('File type:', file.type)
      console.log('Is video:', isVideo)
      
      const storageRef = ref(storage, uploadPath)
      console.log('Storage ref created:', storageRef)
      
      console.log('Starting upload...')
      const uploadTask = uploadBytesResumable(storageRef, fileToUpload)
      
      // Monitor upload progress with real-time updates
      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // Calculate progress percentage
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            console.log(`📤 Upload progress: ${progress}% (${snapshot.bytesTransferred}/${snapshot.totalBytes} bytes)`)
            
            if (onProgress) {
              onProgress(progress)
            }
            
            // Log state changes
            switch (snapshot.state) {
              case 'paused':
                console.log('⏸️ Upload paused')
                break
              case 'running':
                console.log('▶️ Upload running')
                break
            }
          },
          (error) => {
            console.error('❌ Upload error:', error)
            reject(error)
          },
          () => {
            console.log('✅ Upload completed!')
            resolve()
          }
        )
      })

      const downloadURL = await getDownloadURL(storageRef)

      // Get image dimensions if it's an image
      let dimensions
      if (file.type.startsWith('image/')) {
        dimensions = await getImageDimensions(file)
      }

      // Get video dimensions if it's a video
      if (isVideo) {
        try {
          dimensions = await getVideoDimensions(file)
        } catch (error) {
          console.warn('Could not get video dimensions:', error)
        }
      }

      // Create media file record
      const mediaFileId = `media_${timestamp}`
      const mediaFile: Omit<MediaFile, 'id'> = {
        filename,
        originalName: file.name,
        url: downloadURL,
        type: getFileType(file.type),
        mimeType: file.type,
        size: fileToUpload.size,
        dimensions,
        uploadedAt: new Date(),
        uploadedBy: user.email || 'Unknown',
        tags: [],
        usedIn: [],
        folder: 'uploads',
        ...(isVideo && {
          originalUploadPath: uploadPath,
          isProcessing: true,
          compressionStatus: 'pending',
          compressionProgress: 0
        })
      }

      await setDoc(doc(db, 'media', mediaFileId), {
        ...mediaFile,
        uploadedAt: serverTimestamp()
      })

      if (isVideo) {
        toast.success('Video uploaded! Compression in progress...')
      } else {
        toast.success('File uploaded successfully!')
      }
      
      return {
        id: mediaFileId,
        ...mediaFile
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
      return null
    }
  }

  const deleteFile = async (mediaFile: MediaFile): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to delete files')
      return false
    }

    try {
      // Check if file is being used
      if (mediaFile.usedIn.length > 0) {
        const confirmDelete = window.confirm(
          `This file is being used in ${mediaFile.usedIn.length} place(s). Are you sure you want to delete it?`
        )
        if (!confirmDelete) return false
      }

      // Delete from Firebase Storage
      const storageRef = ref(storage, `media/${mediaFile.filename}`)
      await deleteObject(storageRef)

      // Delete from Firestore
      await deleteDoc(doc(db, 'media', mediaFile.id))

      toast.success('File deleted successfully!')
      return true
    } catch (error) {
      console.error('Error deleting file:', error)
      toast.error('Failed to delete file')
      return false
    }
  }

  const updateFile = async (
    fileId: string, 
    updates: Partial<Pick<MediaFile, 'altText' | 'description' | 'tags' | 'folder'>>
  ): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to update files')
      return false
    }

    try {
      await updateDoc(doc(db, 'media', fileId), updates)
      toast.success('File updated successfully!')
      return true
    } catch (error) {
      console.error('Error updating file:', error)
      toast.error('Failed to update file')
      return false
    }
  }

  const addUsage = async (fileId: string, usageId: string): Promise<boolean> => {
    try {
      const mediaFile = mediaFiles.find(f => f.id === fileId)
      if (!mediaFile) return false

      const updatedUsage = [...new Set([...mediaFile.usedIn, usageId])]
      await updateDoc(doc(db, 'media', fileId), { usedIn: updatedUsage })
      return true
    } catch (error) {
      console.error('Error adding usage:', error)
      return false
    }
  }

  const removeUsage = async (fileId: string, usageId: string): Promise<boolean> => {
    try {
      const mediaFile = mediaFiles.find(f => f.id === fileId)
      if (!mediaFile) return false

      const updatedUsage = mediaFile.usedIn.filter(id => id !== usageId)
      await updateDoc(doc(db, 'media', fileId), { usedIn: updatedUsage })
      return true
    } catch (error) {
      console.error('Error removing usage:', error)
      return false
    }
  }

  const syncWithStorage = async (): Promise<{ removed: number; errors: string[] }> => {
    if (!user) {
      toast.error('You must be logged in to sync media')
      return { removed: 0, errors: ['Not authenticated'] }
    }

    const errors: string[] = []
    let removed = 0

    try {
      console.log(`Checking ${mediaFiles.length} media files...`)
      
      for (const mediaFile of mediaFiles) {
        try {
          // Try to get the download URL - if it fails, the file doesn't exist
          const storageRef = ref(storage, `media/${mediaFile.filename}`)
          await getDownloadURL(storageRef)
          // File exists, all good
        } catch (error: any) {
          // File doesn't exist in storage, remove from Firestore
          if (error.code === 'storage/object-not-found') {
            console.log(`Removing orphaned record: ${mediaFile.filename}`)
            await deleteDoc(doc(db, 'media', mediaFile.id))
            removed++
          } else {
            errors.push(`Error checking ${mediaFile.filename}: ${error.message}`)
          }
        }
      }

      if (removed > 0) {
        toast.success(`Sync complete! Removed ${removed} orphaned record(s)`)
      } else {
        toast.success('All media files are in sync!')
      }

      return { removed, errors }
    } catch (error) {
      console.error('Error syncing media:', error)
      toast.error('Failed to sync media library')
      return { removed, errors: [error instanceof Error ? error.message : 'Unknown error'] }
    }
  }

  return {
    mediaFiles,
    loading,
    error,
    uploadFile,
    deleteFile,
    updateFile,
    addUsage,
    removeUsage,
    syncWithStorage
  }
}

// Helper functions
const getFileType = (mimeType: string): MediaFile['type'] => {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  return 'document'
}

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      })
    }
    img.onerror = reject
    img.src = URL.createObjectURL(file)
  })
}

const getVideoDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight
      })
      URL.revokeObjectURL(video.src)
    }
    video.onerror = reject
    video.src = URL.createObjectURL(file)
  })
}
